import React, { useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { RowNode } from 'ag-grid-community';
import { Box, styled } from '@mui/material';

import Loading from 'components/Loading';

import { GridApi, GridReadyEvent } from 'ag-grid-community';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import { translateBlock } from 'i18n';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import {
    ResourceBudgetProposalAssociationT,
    ResourceType,
} from 'routes/BudgetModule/config/types';

const StyGrid = styled(Box, {
    name: 'StyGrid',
})({});

const AssociationsGrid: React.FC<{
    setAssociations: DispatchState<ResourceBudgetProposalAssociationT[]>;
    initialAssociations?: ResourceBudgetProposalAssociationT[];
    resourcesWithBp: ResourceBudgetProposalAssociationT[];
    isLoading: boolean;
    budgetProposalByField: ResourceType;
}> = ({
    setAssociations,
    initialAssociations,
    resourcesWithBp,
    isLoading,
    budgetProposalByField,
}) => {
    const { t } = useTranslation();
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    const onGridReady = (params: GridReadyEvent) => {
        setGridApi(params.api);
        if (initialAssociations && params.api)
            params.api.forEachNode((node: RowNode) => {
                const { data }: { data: ResourceBudgetProposalAssociationT } =
                    node;

                const nodeResourceSoftwareId =
                    data.resource?.resourceSoftwareId;
                const nodeBpId = data.budgetProposal.id;

                initialAssociations.forEach((initialAssociation) => {
                    const initialAssociationResourceId =
                        initialAssociation.resource?.resourceSoftwareId;
                    const initialAssociationBpId =
                        initialAssociation.budgetProposal.id;
                    if (
                        nodeResourceSoftwareId ===
                            initialAssociationResourceId &&
                        nodeBpId === initialAssociationBpId
                    ) {
                        node.setSelected(true);
                    }
                });
            });
    };

    const onSelectionChanged = () => {
        if (!gridApi) return;
        const selectedRows = gridApi.getSelectedRows();
        setAssociations(selectedRows);
    };

    const columnDefs = [
        {
            headerName: translateBlock(budgetProposalByField),
            field: 'resource',
            minWidth: 250,
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            checkboxSelection: true,
            filter: 'agTextColumnFilter',
            floatingFilterComponent: 'textFloatingFilter',
            floatingFilter: true,
            suppressFloatingFilterButton: true,
            valueGetter: ({
                data,
            }: {
                data: ResourceBudgetProposalAssociationT;
            }) => data?.resource?.resourceName,
        },
        {
            headerName: t('createDraftOperation.budgetProposal'),
            field: 'budgetProposal',
            filter: 'agTextColumnFilter',
            floatingFilterComponent: 'textFloatingFilter',
            floatingFilter: true,
            suppressFloatingFilterButton: true,
            minWidth: 500,
            valueGetter: ({
                data,
            }: {
                data: ResourceBudgetProposalAssociationT;
            }) => data?.budgetProposal?.name,
        },
    ];
    const defaultColDef = {
        flex: 1,
        resizable: true,
        rowSelection: 'multiple',
    };

    return (
        <StyGrid className="ag-theme-material">
            {isLoading ? (
                <Loading />
            ) : (
                <AgGridReact
                    components={{
                        textFloatingFilter: TextFloatingFilter,
                    }}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    suppressRowClickSelection
                    rowSelection="multiple"
                    pagination
                    paginationPageSize={8}
                    rowData={resourcesWithBp || []}
                    domLayout="autoHeight"
                    onGridReady={onGridReady}
                    onSelectionChanged={onSelectionChanged}
                />
            )}
        </StyGrid>
    );
};

export default memo(
    AssociationsGrid,
    (prevProps, nextProps) =>
        JSON.stringify(prevProps.resourcesWithBp) ===
            JSON.stringify(nextProps.resourcesWithBp) &&
        prevProps.isLoading === nextProps.isLoading &&
        prevProps.budgetProposalByField === nextProps.budgetProposalByField,
);
